/* Interface describing global state */

/*
Currently, we separate our redux state into three separate components:

META: This refers to state which is used to describe a component
    For example: If we have multiple lists of multiple text boxes that need to be differentiated from each other, a
    text box  might have the following META state
        {name: text-box-1
        type: text-box-small
        id: 12
        list-id: 6}

VALUES: This refers to user updates to state on the client side that need to be persisted to the database
    Following the text box example, the VALUE portion of state for the same text box might look like the following
        {12: {text: "This is some test text for the VALUE portion of state"}}

UI: This refers to state that changes how the components on screen are displayed, and does not need to be persisted to
the database. Examples of this include filtering and loading states. E.g:
        {IS_LOADING_TEXT_BOXES: false,
         FILTER_TYPE: text-box-small}
*/

import { ITabField, ITab, ITableItem, ICountryObject, IStateObject, IGroupUsers } from '@logicmanager/common';

export interface IState {
    readonly meta: IMeta;
    readonly ui: IUi;
    readonly values: IValues;
}

export interface IMeta {
    readonly tabs: ITab[] | {[tabId: string]: ITab};
    readonly isLoading: boolean;
    readonly fields: ITabField[] | IFieldsMeta;
    readonly users: any;
    readonly countries: ICountryObject[];
    readonly states: IStateObject[];
    readonly title: string;
    readonly groupUsers: IGroupUsers;
}

export interface IUi {
    readonly isFetching: boolean;
    readonly isSubmitted: boolean;
    readonly status: STATUS;
    readonly tabOpened: ITabStatus;
    readonly hasVisibilityRules: boolean;
    readonly isCaptchaEnabled: boolean;
    readonly useHeaderLogo: boolean;
    readonly logoUrl: string;
}

export interface ITabStatus {
    readonly [tabId: string]: boolean;
}

export enum STATUS {
    Fetching = 'FETCHING',
    Success = 'SUCCESS',
    Failure = 'FAILURE'
}

export interface IFieldsMeta {
    readonly [fieldId: string]: ITabField;
}

export interface IValues {
    readonly fields: IFieldValue;
    readonly captchaResponse: string;
}

export interface IFieldValue {
    readonly [id: string]: IFieldValueObject;
}

export interface IFieldValueEditable {
    [id: string]: IFieldValueObject;
}

export interface IFieldValueObject {
    readonly id?: string;
    readonly tabId?: string;
    readonly value?: any;
    readonly linkType?: string;
    readonly fieldType?: string;
    readonly tableItems?: ITableItem[];
    readonly required?: boolean;
    readonly extra?: string;
    readonly valid?: boolean;
    readonly visible?: boolean;
    readonly incomplete?: boolean;
}
